import { client } from "@noa/api-core";
import { ApiError, networkError, serverError, toApiError } from "./errors";

export interface ApiClientOptions {
  /** Base URL for the API, e.g. "https://api.example.com" or "http://localhost:8000". */
  baseUrl: string;
  /** Returns the current auth token, or null if unauthenticated. Called before every request. */
  getToken: () => Promise<string | null>;
  /** Returns the current locale (e.g. "fr", "en"). Called before every request. */
  getLanguage: () => string;
  /** Called when an unhandled API error occurs (e.g. show a toast). Always receives an {@link ApiError}. */
  onError: (error: ApiError) => void;
  /**
   * Optional fetch implementation passed to both the typed client and the SSE stream. Defaults to
   * the global fetch. Override when the platform needs a non-standard implementation — e.g. inject
   * `expo/fetch` on React Native to enable streaming SSE via `response.body.getReader()`.
   */
  fetch?: typeof fetch;
}

const state: { options: ApiClientOptions | null } = { options: null };

// Register the interceptors once at module load, reading options lazily via `state`.
// SDK functions fall back to the default `client` singleton from `@noa/api-core`; these
// handlers attach to it before any call runs. Registering here (rather than inside
// initApiClient) means re-init (HMR, tests) never stacks duplicate handlers — no guard needed.
client.interceptors.request.use(async (request: Request) => {
  const opts = state.options;
  if (!opts) return request;
  const token = await opts.getToken();
  // Don't set Content-Type here — the SDK handles it. For JSON requests it sets
  // application/json, for multipart it omits the header so the browser adds the boundary.
  request.headers.set("Accept-Language", opts.getLanguage());
  if (token) request.headers.set("Authorization", `Bearer ${token}`);
  return request;
});

// Single funnel for failures: turn any SDK error into an ApiError. Combined with
// `throwOnError: true` on each call, every hook throws a uniform, i18n-ready ApiError.
// `response` is undefined for transport failures (network down, DNS), present otherwise;
// `error` is then the parsed response body.
client.interceptors.error.use((error: unknown, response: Response | undefined) => {
  if (error instanceof ApiError) return error;
  return response ? serverError(response.status, response.statusText, error) : networkError();
});

/**
 * Configure the shared API client. Must be called once at app startup before any SDK function
 * or hook runs. Injects platform-specific services (auth, locale, errors).
 *
 * Seeds the default `client` singleton's transport config (`setConfig`). The interceptors,
 * registered once at module load, read `options` lazily — so calling this again (HMR, tests)
 * updates the injected services in place.
 */
export const initApiClient = (options: ApiClientOptions): void => {
  state.options = options;
  client.setConfig({ baseUrl: options.baseUrl, fetch: options.fetch });
};

/**
 * Returns the injected error handler, falling back to console.error when
 * initApiClient() has not been called yet (e.g. tests).
 */
const getErrorHandler = (): ((error: ApiError) => void) =>
  state.options?.onError ?? ((e) => console.error("[api-client] unhandled error:", e));

/**
 * Reports an error through the injected onError handler (console.error fallback).
 * Accepts `unknown` and normalises to {@link ApiError} before dispatching — safe to use
 * in catch blocks and react-query `onError` callbacks where the type is uncontrolled.
 */
export const reportError = (error: unknown): void => getErrorHandler()(toApiError(error));

/**
 * Builds the standard auth + language headers. Used both for SSE requests (which bypass the
 * typed client) and for app-level authenticated fetches (e.g. image downloads).
 * Throws if initApiClient() has not been called.
 */
export const buildAuthHeaders = async (): Promise<Record<string, string>> => {
  if (!state.options) throw new Error("[api-client] buildAuthHeaders() called before initApiClient()");

  const token = await state.options.getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "Accept-Language": state.options.getLanguage(),
  };

  if (token) headers.Authorization = `Bearer ${token}`;

  return headers;
};

/**
 * Returns the configured base URL. Throws if initApiClient() has not been called.
 * Used internally by utilities that need to build absolute API URLs (e.g. document-url).
 */
export const getBaseUrl = (): string => {
  if (!state.options) throw new Error("[api-client] getBaseUrl() called before initApiClient()");

  return state.options.baseUrl;
};

/**
 * Bundles everything an SSE request needs from the private client state — base URL, the optional
 * platform fetch override, and the auth + language headers — in a single call, so SSE callers
 * don't reach for three separate accessors. Throws if initApiClient() has not been called.
 */
export const getSseRequestConfig = async (): Promise<{
  baseUrl: string;
  fetch: typeof fetch | undefined;
  headers: Record<string, string>;
}> => {
  if (!state.options) throw new Error("[api-client] getSseRequestConfig() called before initApiClient()");

  return {
    baseUrl: state.options.baseUrl,
    fetch: state.options.fetch,
    headers: await buildAuthHeaders(),
  };
};
